import type { BaseAsset } from '@lumen5/luminary';
import type { GetRootState } from 'js/src/libs/RootState';
import type Luminary from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import { getNonRecommendableAssetNames } from '../../video-state/serialization/assetSerializer';
import type { LintFinding } from '../videoLinter';

/**
 * How far past an edge the text has to reach before it counts. A box flush to the
 * edge is a deliberate full-bleed design, and rounding in the bounds leaves a
 * sub-pixel sliver on plenty of healthy scenes, so only a gap wide enough to
 * swallow a glyph is worth reporting.
 */
const OFFSCREEN_TOLERANCE_PX = 8;

type Edge = 'left' | 'top' | 'right' | 'bottom';

type Edges = Partial<Record<Edge, number>>;

/**
 * How far the box spills past each edge of the frame, in reference-resolution
 * pixels. Zero or negative means that edge is inside the frame. Edges whose
 * measurement isn't trustworthy are left out rather than reported as zero.
 */
function overflowPerEdge(
    bounds: { x: number; y: number; width: number; height: number },
    resolution: { width: number; height: number },
    { measuresHeight }: { measuresHeight: boolean },
): Edges {
    const edges: Edges = {
        left: -bounds.x,
        right: bounds.x + bounds.width - resolution.width,
    };
    if (measuresHeight) {
        edges.top = -bounds.y;
        edges.bottom = bounds.y + bounds.height - resolution.height;
    }
    return edges;
}

/**
 * The box the text occupies, and whether its vertical extent is a real
 * measurement.
 *
 * `boundsWithoutAnimation` is the rendered glyph box with the intro/outro
 * animation's travel excluded — an asset that flies in from off-frame is not
 * offscreen once it lands — so it is the honest answer to "where does this text
 * sit". It is only populated once the renderer has drawn the asset and reports
 * an empty rect otherwise, which is the common case outside a live canvas.
 *
 * The declared `maxWidth × maxHeight` envelope stands in for it then, but only
 * horizontally. Luminary rewrites an unset `maxHeight` to the full frame height
 * in the constructor, and an undrawn `TextAsset` reports that same value as its
 * occupied height, so a vertical reading off the envelope flags every asset
 * below the top edge as hanging off the bottom. Width has no such trap: an
 * unset `maxWidth` becomes the full frame width, which overflows nothing at
 * x = 0, and a box that is genuinely too wide for where it is anchored is the
 * AP-1130 failure this check exists to catch.
 */
function occupiedBounds(asset: BaseAsset) {
    const rendered = asset.boundsWithoutAnimation;
    if (rendered.width > 0 && rendered.height > 0) {
        return { bounds: rendered, measuresHeight: true };
    }
    return { bounds: asset.approximateMaxBounds, measuresHeight: false };
}

function describeOverflow(edges: Edges): string {
    return (['left', 'top', 'right', 'bottom'] as const)
        .map((edge) => ({ edge, overflow: edges[edge] ?? 0 }))
        .filter(({ overflow }) => overflow > OFFSCREEN_TOLERANCE_PX)
        .map(({ edge, overflow }) => `${Math.round(overflow)}px past the ${edge} edge`)
        .join(' and ');
}

/**
 * Error: a text asset's box reaches outside the video frame, so the words that
 * land there are cut off by the edge of the canvas and never rendered. Unlike
 * `text_clipping`, which compares the text against its own box, this compares
 * the box against the frame — text can fit its box perfectly and still be
 * unreadable because the box itself hangs off the canvas (AP-1130).
 *
 * The usual cause is a width set without accounting for where the box is
 * anchored: widening an asset that is left-anchored inside a right-hand panel
 * pushes its tail off the frame while its stored geometry still looks sane.
 */
export function checkTextOffscreen(luminary: Luminary, getState: GetRootState): LintFinding[] {
    const findings: LintFinding[] = [];
    const resolution = luminary.referenceResolution;

    luminary.scenes.forEach((scene) => {
        const nonRecommendable = getNonRecommendableAssetNames(scene, getState());
        const textAssets = scene
            .getAssetsByType(VideoData.TEXT_ASSET_TYPE)
            .filter(
                (asset) =>
                    asset.isEditable && asset.text?.trim() && !nonRecommendable.has(asset.name),
            );

        textAssets.forEach((asset) => {
            const { bounds, measuresHeight } = occupiedBounds(asset);
            const overflow = describeOverflow(
                overflowPerEdge(bounds, resolution, { measuresHeight }),
            );
            if (!overflow) {
                return;
            }
            findings.push({
                check: 'text_offscreen',
                severity: 'error',
                sceneId: scene.id,
                assetName: asset.name,
                message: `Text "${asset.text.trim()}" on asset "${asset.name}" extends ${overflow} of the ${resolution.width}x${resolution.height} frame, so part of it is cut off and never shown. Narrow the box, move it back inside the frame, or lower the font size. Note that widening a box only helps when it is centred — widening one anchored against a panel edge pushes it further off the frame.`,
            });
        });
    });

    return findings;
}
